import { Controller } from '@hotwired/stimulus';

// Contrôleur pour valider le montant HT saisi en temps réel
// Empêche de dépasser 100% du montant total du contrat
//
// Two input paths share the same ceiling logic via #evaluate(candidateAmount):
//   - Legacy single-amount path: `input` event on `amountField` →
//     validateAmount() reads the field value.
//   - Multi-invoice path: `invoice-lines:total-changed@window` →
//     onTotalChanged() reads `event.detail.totalHt`.
//
// The ceiling formula mirrors the server-side validator at
// app/models/concerns/work_progress_statements/financial_calculations.rb:65
// (`useProrataValue ? contractTotal : contractTotal + totalAdvancesAmount`)
// so the client and the backend agree in both prorata and non-prorata modes.
export default class extends Controller {
  static targets = ['amountField', 'errorBox', 'errorMessage', 'modeInput'];
  static values = {
    contractTotal: Number, // Total du contrat HT (marché de base + TS)
    previousCumul: Number, // Cumul HT de la situation précédente
    totalAdvancesAmount: Number, // Montant total acomptes HT
    useProrata: Boolean, // Contract#advance_deduction_prorata?
  };

  connect() {
    if (this.hasAmountFieldTarget) this.validateAmount();
    // No need to prime the multi-invoice path here: Stimulus initializes
    // outer (form) controllers before inner (#invoice-lines-table); when the
    // inner connect() calls dispatchTotal() (invoice_lines_table_controller.js),
    // the resulting window event lands on the listener wired in _form.html.erb.
  }

  // Public state read by wps-form#beforeSubmit (Enter-key safety net).
  // Returns true when ANY errorBox slot is currently rendered visible.
  isOverrun() {
    return this.errorBoxTargets.some((el) => !el.classList.contains('hidden'));
  }

  /**
   * Returns the first non-hidden errorBox slot, or undefined if none is shown.
   * Used by `wps-form#beforeSubmit` to scroll the visible warning into view.
   *
   * @returns {HTMLElement|undefined}
   */
  visibleErrorBox() {
    return this.errorBoxTargets.find((el) => !el.classList.contains('hidden'));
  }

  /**
   * Returns the active mode scope based on the legacy amount field's runtime
   * state: 'multi' when the field is missing OR readonly (≥1 invoice attached);
   * 'legacy' otherwise (single-amount path — acompte / pre-attach).
   *
   * @returns {'multi'|'legacy'}
   */
  #activeMode() {
    if (!this.hasAmountFieldTarget || !this.#amountFieldEditable()) return 'multi';
    return 'legacy';
  }

  // Legacy single-amount path — input event on amountField.
  // HTML5 type="number" enforces dot decimals, so parseFloat is locale-safe.
  validateAmount() {
    // Multi-invoice mode: legacy amountField is removed from DOM; the
    // multi-invoice path handles the ceiling check via onTotalChanged.
    if (!this.hasAmountFieldTarget) return;

    const value = parseFloat(this.amountFieldTarget.value) || 0;
    this.#evaluate(value);
  }

  // Multi-invoice path — invoice-lines:total-changed@window.
  // `event.detail.totalHt` is already a JS number (no locale parsing needed).
  // Performs the same ceiling check as validateAmount() so the client matches
  // the server-side `cumulative_ht_within_contract_limit` validator.
  //
  // The legacy `amountField` target is ALSO present in multi-invoice mode (as
  // a readonly mirror of the lines-table footer total — see _form.html.erb).
  // So we can't early-return on `hasAmountFieldTarget` alone; we must check
  // `data-amount-mode`. When the field is editable (no rows attached, legacy
  // single-amount mode), `validateAmount` owns the path via the field's input
  // event. When it's readonly (multi-invoice mode), the readonly write in
  // `wps-form#onTotalChanged` does not fire an input event, so this listener
  // is the only path that runs the ceiling check.
  onTotalChanged(event) {
    if (this.hasAmountFieldTarget && this.#amountFieldEditable()) return;
    const totalHt = event?.detail?.totalHt ?? 0;
    this.#evaluate(totalHt);
  }

  #amountFieldEditable() {
    return this.amountFieldTarget.dataset.amountMode !== 'readonly';
  }

  showError(newCumul, ceiling) {
    if (!this.hasErrorBoxTarget) return;

    const advancesAmount = this.totalAdvancesAmountValue;
    const contractTotal = this.contractTotalValue;
    // Show the advance breakdown only when the ceiling actually includes it
    // (non-prorata mode) AND there is one — keeps the legacy copy verbatim.
    const hasAdvance = !this.useProrataValue && advancesAmount > 0;
    const formattedCumul = this.formatCurrency(newCumul);
    const formattedTotal = this.formatCurrency(contractTotal);
    const formattedAdvances = this.formatCurrency(advancesAmount);
    const formattedExcess = this.formatCurrency(newCumul - ceiling);

    const totalLabel = !hasAdvance
      ? `(<strong>${formattedTotal}</strong>)`
      : ` et de l'acompte (<strong>${formattedTotal} + ${formattedAdvances}</strong>)`;
    const html = `
      Le cumul HT (<strong>${formattedCumul}</strong>)
      dépasse le total du contrat ${totalLabel}.
      <br>
      Dépassement : <strong class="text-danger-700">${formattedExcess}</strong>
    `;

    // Write the same body into both errorMessage slots — keeps the DOM
    // symmetric so a runtime mode flip (drop PDF / remove last row) reveals a
    // pre-populated message instead of an empty box.
    this.errorMessageTargets.forEach((el) => {
      el.innerHTML = html;
    });

    // Reveal only the slot whose `data-mode-scope` matches the current mode;
    // hide its twin so a single message is on screen.
    const activeMode = this.#activeMode();
    this.errorBoxTargets.forEach((el) => {
      el.classList.toggle('hidden', el.dataset.modeScope !== activeMode);
    });
  }

  hideError() {
    if (!this.hasErrorBoxTarget) return;
    this.errorBoxTargets.forEach((el) => el.classList.add('hidden'));
  }

  // Submit-button disabling is now owned by `wps-form#updateSubmitButton`,
  // which aggregates multiple disable reasons (pending extraction, missing
  // statement_type, overrun, sum-mismatch). amount-validator just announces
  // its own state via an event; `wps-form#onOverrunChanged` re-evaluates.
  #notifyOverrunChanged(overrun) {
    this.dispatch('overrun-changed', { detail: { overrun } });
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }

  // Single source of truth for the ceiling — mirrors server
  // app/models/concerns/work_progress_statements/financial_calculations.rb:65
  // In prorata mode the server doesn't add advances to the limit (advances
  // are amortized linearly over the contract); otherwise advances bump it.
  #ceiling() {
    return this.useProrataValue
      ? this.contractTotalValue
      : this.contractTotalValue + this.totalAdvancesAmountValue;
  }

  // Shared evaluation path: cumul = previous + candidate; compare to ceiling.
  // Both validateAmount() (legacy) and onTotalChanged() (multi-invoice) feed
  // here so the two paths can never drift out of sync.
  #evaluate(candidateAmount) {
    const newCumul = this.previousCumulValue + candidateAmount;
    const ceiling = this.#ceiling();
    if (newCumul > ceiling) {
      this.showError(newCumul, ceiling);
      this.#notifyOverrunChanged(true);
    } else {
      this.hideError();
      this.#notifyOverrunChanged(false);
    }
  }
}
